import React, { useState, useEffect, useRef } from 'react';
import './AdminNormativa.css';
import { getApiUrl } from '../../config/api';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import { FaFolder, FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileImage, FaFileAlt, FaFileArchive, FaFile, FaLock, FaCalendarAlt, FaTrash, FaCog, FaPlus, FaFileUpload, FaHome, FaEyeSlash } from 'react-icons/fa';

const AdminNormativa = () => {
    const [fileTree, setFileTree] = useState([]);
    const [currentFiles, setCurrentFiles] = useState([]);
    const [currentPath, setCurrentPath] = useState([]); // Array of folder names
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [availableRoles, setAvailableRoles] = useState([]); // Store fetched roles

    // Modals
    const [showFolderModal, setShowFolderModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);

    // Inputs
    const [newFolderName, setNewFolderName] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    // Settings Form
    const [settingsForm, setSettingsForm] = useState({
        is_protected: false,
        is_visible: true,
        password: '',
        visible_from: '',
        visible_until: '',
        password: '',
        visible_from: '',
        visible_until: '',
        allowed_roles: [], // Changed to array
        deletePassword: false
    });

    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchFiles();
        fetchRoles();
    }, []);

    const fetchRoles = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(getApiUrl('/roles'), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setAvailableRoles(data);
            }
        } catch (error) {
            console.error("Error fetching roles:", error);
        }
    };

    useEffect(() => {
        // Recalculate current view based on path
        updateCurrentView();
    }, [fileTree, currentPath]);

    const fetchFiles = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(getApiUrl('/admin/files'), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error("Error cargando archivos");
            const data = await response.json();
            setFileTree(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const updateCurrentView = () => {
        let current = fileTree;
        for (const folderName of currentPath) {
            const found = current.find(f => f.name === folderName && f.type === 'directory');
            if (found && found.children) {
                current = found.children;
            } else {
                current = [];
                break;
            }
        }
        setCurrentFiles(current);
    };

    const handleNavigate = (folderName) => {
        setCurrentPath([...currentPath, folderName]);
    };

    const handleNavigateUp = (index) => {
        if (index === -1) setCurrentPath([]);
        else setCurrentPath(currentPath.slice(0, index + 1));
    };

    // --- ACTIONS ---

    const handleCreateFolder = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('authToken');
            const relativePath = currentPath.join('/');

            const response = await fetch(getApiUrl('/admin/files/folder'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ path: relativePath, name: newFolderName })
            });

            if (response.ok) {
                setShowFolderModal(false);
                setNewFolderName('');
                fetchFiles();
            } else {
                alert("Error creando carpeta");
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleUpload = async (e) => {
        const files = e.target.files;
        if (!files.length) return;

        setUploading(true);
        const formData = new FormData();
        const relativePath = currentPath.join('/');

        // Agregar path como query param o campo extra si el backend lo soporta
        // Mi backend espera query param 'folder' en storage config?
        // Revisando index.js: if (req.query.folder)...

        // Pero multer procesa primero. Asique debo mandarlo como query param en la URL.
        const url = getApiUrl(`/admin/files/upload?folder=${encodeURIComponent(relativePath)}`);

        for (let i = 0; i < files.length; i++) {
            formData.append('files', files[i]);
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (response.ok) {
                fetchFiles();
            } else {
                alert("Error subiendo archivos");
            }
        } catch (err) {
            console.error(err);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (file) => {
        if (!window.confirm(`¿Seguro que quieres borrar ${file.name}?`)) return;

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(getApiUrl('/admin/files'), {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ path: file.path })
            });

            if (response.ok) fetchFiles();
            else alert("Error borrando archivo");
        } catch (err) {
            console.error(err);
        }
    };

    // --- METADATA SETTINGS ---

    const openSettings = (file) => {
        setSelectedFile(file);
        const meta = file.metadata || {};

        let currentRoles = [];
        if (meta.allowed_roles) {
            try {
                if (Array.isArray(meta.allowed_roles)) {
                    currentRoles = meta.allowed_roles;
                } else {
                    const parsed = JSON.parse(meta.allowed_roles);
                    currentRoles = Array.isArray(parsed) ? parsed : [];
                }
            } catch (e) {
                // If it's a raw string (e.g. "admin"), wrap in array
                currentRoles = typeof meta.allowed_roles === 'string' ? [meta.allowed_roles] : [];
            }
        }

        setSettingsForm({
            is_protected: meta.is_protected ? true : false,
            is_visible: meta.is_visible !== false && meta.is_visible !== 0,
            password: '',
            visible_from: meta.visible_from ? new Date(meta.visible_from).toISOString().slice(0, 16) : '',
            visible_until: meta.visible_until ? new Date(meta.visible_until).toISOString().slice(0, 16) : '',
            allowed_roles: currentRoles, // Set as array
            deletePassword: false
        });
        setShowSettingsModal(true);
    };

    const handleSaveSettings = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('authToken');

            // Parsear roles (Ya viene como array del form state)
            const rolesArray = settingsForm.allowed_roles;



            const body = {
                path: selectedFile.path,
                is_protected: settingsForm.is_protected,
                is_visible: settingsForm.is_visible,
                visible_from: settingsForm.visible_from || null,
                visible_until: settingsForm.visible_until || null,
                allowed_roles: rolesArray
            };

            if (settingsForm.password) {
                body.password = settingsForm.password;
            } else if (!settingsForm.is_protected) {
                // Si desprotege, enviamos cadena vacia para que el backend borre el pass si quiere,
                // O confiamos en que is_protected=false deshabilite el check
                // En mi backend logic: is_protected es un flag. Password hash se mantiene a menos que envíe "".
                // Si quiero "limpiar" la contraseña, debo enviar "".
                // Si el usuario desmarca "Proteger", deberíamos limpiar la pass? O guardarla por si vuelve a marcar?
                // Mejor UX: Si desmarca, el archivo es publico. La pass da igual.
                // Pero si quiero borrar la pass explicitamente:
                // Si no escribe password nuevo, enviamos undefined.
                // Si queremos borrar, enviamos "".
            }

            // Lógica Frontend para Password:
            // Si el usuario desmarca "Proteger con contraseña", enviamos is_protected: false.
            // Si el usuario clicka "Eliminar contraseña" (nuevo btn), enviamos password: "".

            // Corrección sobre el plan: settingsForm.password será "" by default.
            // Usaremos un flag especial o valor mágico para borrar?
            // Mejor: Si settingsForm.deletePassword === true, enviar password: "".

            if (settingsForm.deletePassword) {
                body.password = "";
            } else if (settingsForm.password) {
                body.password = settingsForm.password;
            }

            const response = await fetch(getApiUrl('/admin/files/meta'), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            if (response.ok) {
                setShowSettingsModal(false);
                fetchFiles();
            } else {
                alert("Error guardando ajustes");
            }
        } catch (err) {
            console.error(err);
        }
    };

    const getFileIcon = (fileName) => {
        const ext = fileName.split('.').pop().toLowerCase();
        switch (ext) {
            case 'pdf': return <FaFilePdf />;
            case 'doc': case 'docx': return <FaFileWord />;
            case 'xls': case 'xlsx': return <FaFileExcel />;
            case 'ppt': case 'pptx': return <FaFilePowerpoint />;
            case 'jpg': case 'jpeg': case 'png': case 'gif': return <FaFileImage />;
            case 'txt': return <FaFileAlt />;
            case 'zip': case 'rar': case '7z': return <FaFileArchive />;
            default: return <FaFile />;
        }
    };

    return (
        <AdminLayout>
            <div className="admin-normativa-container">
                <h2 className="normativa-page-title">Gestión de Normativas</h2>

                <div className="normativa-toolbar">
                    <button className="toolbar-btn btn-primary" onClick={() => setShowFolderModal(true)}>
                        <FaPlus /> Nueva Carpeta
                    </button>
                    <div style={{ position: 'relative' }}>
                        <input
                            type="file"
                            multiple
                            style={{ display: 'none' }}
                            ref={fileInputRef}
                            onChange={handleUpload}
                        />
                        <button className="toolbar-btn btn-secondary" onClick={() => fileInputRef.current.click()} disabled={uploading}>
                            <FaFileUpload /> {uploading ? 'Subiendo...' : 'Subir Archivos'}
                        </button>
                    </div>
                </div>

                <div className="file-explorer">
                    <div className="breadcrumb-admin">
                        <span onClick={() => handleNavigateUp(-1)}><FaHome /> Inicio</span>
                        {currentPath.map((folder, index) => (
                            <React.Fragment key={index}>
                                <span className="separator">/</span>
                                <span onClick={() => handleNavigateUp(index)}>{folder}</span>
                            </React.Fragment>
                        ))}
                    </div>

                    {loading ? <p>Cargando...</p> : (
                        <div className="file-list">
                            {currentFiles.length === 0 && <div className="empty-state">Esta carpeta está vacía.</div>}
                            {currentFiles.map((file, idx) => (
                                <div key={idx} className="file-card" onClick={() => file.type === 'directory' ? handleNavigate(file.name) : null}>
                                    <div className={`file-icon ${file.type === 'directory' ? 'folder' : 'file'}`}>
                                        {file.type === 'directory' ? <FaFolder /> : getFileIcon(file.name)}
                                    </div>
                                    <div className="file-name">{file.name}</div>
                                    <div className="file-meta-icons">
                                        {file.metadata?.is_protected ? <FaLock title="Protegido" /> : null}
                                        {(file.metadata?.is_visible === false || file.metadata?.is_visible === 0) ? (
                                            <FaEyeSlash title="No visible para el público" style={{ marginLeft: '5px', color: '#999' }} />
                                        ) : null}
                                        {(file.metadata?.visible_from || file.metadata?.visible_until) ? (
                                            <FaCalendarAlt title="Programado" className="is-scheduled" />
                                        ) : null}
                                    </div>
                                    <div className="file-actions" onClick={(e) => e.stopPropagation()}>
                                        <button className="action-btn" onClick={() => openSettings(file)} title="Configurar"><FaCog /></button>
                                        <button className="action-btn delete" onClick={() => handleDelete(file)} title="Borrar"><FaTrash /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Folder Modal */}
                {showFolderModal && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <div className="modal-header"><h3>Crear Carpeta</h3></div>
                            <form onSubmit={handleCreateFolder}>
                                <div className="form-group">
                                    <label>Nombre de la carpeta</label>
                                    <input
                                        className="form-control"
                                        value={newFolderName}
                                        onChange={e => setNewFolderName(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <div className="modal-actions">
                                    <button type="button" className="btn-secondary" onClick={() => setShowFolderModal(false)}>Cancelar</button>
                                    <button type="submit" className="btn-primary">Crear</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Settings Modal */}
                {showSettingsModal && selectedFile && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <div className="modal-header"><h3>Ajustes: {selectedFile.name}</h3></div>
                            <form onSubmit={handleSaveSettings}>
                                <div className="toggle-wrapper" onClick={() => setSettingsForm({ ...settingsForm, is_protected: !settingsForm.is_protected })}>
                                    <span className="toggle-label">Proteger con Contraseña</span>
                                    <label className="toggle-switch" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={settingsForm.is_protected}
                                            onChange={(e) => setSettingsForm({ ...settingsForm, is_protected: e.target.checked })}
                                        />
                                        <span className="slider"></span>
                                    </label>
                                </div>

                                <div className="toggle-wrapper" onClick={() => setSettingsForm({ ...settingsForm, is_visible: !settingsForm.is_visible })}>
                                    <span className="toggle-label">Visible para el público?</span>
                                    <label className="toggle-switch" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={settingsForm.is_visible}
                                            onChange={(e) => setSettingsForm({ ...settingsForm, is_visible: e.target.checked })}
                                        />
                                        <span className="slider"></span>
                                    </label>
                                </div>

                                {settingsForm.is_protected && (
                                    <div className="form-group">
                                        <label>Contraseña (Dejar en blanco para no cambiar)</label>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <input
                                                className="form-control"
                                                type="password"
                                                value={settingsForm.password}
                                                onChange={e => setSettingsForm({ ...settingsForm, password: e.target.value, deletePassword: false })}
                                                placeholder="Nueva contraseña"
                                            />
                                            {selectedFile.metadata && selectedFile.metadata.has_password && (
                                                <button type="button" className="btn-secondary" style={{ whiteSpace: 'nowrap' }}>
                                                    Tiene Contraseña
                                                </button>
                                            )}
                                            {/* Mejor UI: Si tiene contraseña, mostrar un boton para borrarla */}
                                            {selectedFile.metadata && selectedFile.metadata.has_password && !settingsForm.deletePassword && (
                                                <button
                                                    type="button"
                                                    className="btn-danger"
                                                    onClick={() => setSettingsForm({ ...settingsForm, deletePassword: true, password: '' })}
                                                    title="Eliminar la contraseña actual"
                                                >
                                                    <FaTrash />
                                                </button>
                                            )}
                                            {settingsForm.deletePassword && <span style={{ color: '#ff4444', alignSelf: 'center' }}>Se eliminará</span>}
                                        </div>
                                    </div>
                                )}

                                <hr style={{ borderColor: '#3e3e50', margin: '20px 0' }} />

                                <div className="form-group">
                                    <label>Visible Desde</label>
                                    <input
                                        className="form-control"
                                        type="datetime-local"
                                        value={settingsForm.visible_from}
                                        onChange={e => setSettingsForm({ ...settingsForm, visible_from: e.target.value })}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Visible Hasta</label>
                                    <input
                                        className="form-control"
                                        type="datetime-local"
                                        value={settingsForm.visible_until}
                                        onChange={e => setSettingsForm({ ...settingsForm, visible_until: e.target.value })}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Roles Permitidos</label>
                                    <div className="roles-checkbox-list">
                                        {availableRoles.length > 0 ? availableRoles.map((role) => (
                                            <div key={role.id} className="role-checkbox-item">
                                                <label className="role-checkbox-label">
                                                    <input
                                                        type="checkbox"
                                                        className="role-checkbox-input"
                                                        value={role.name}
                                                        checked={settingsForm.allowed_roles.includes(role.name)}
                                                        onChange={(e) => {
                                                            const roleName = e.target.value;
                                                            let newRoles = [...settingsForm.allowed_roles];
                                                            if (e.target.checked) {
                                                                newRoles.push(roleName);
                                                            } else {
                                                                newRoles = newRoles.filter(r => r !== roleName);
                                                            }
                                                            setSettingsForm({ ...settingsForm, allowed_roles: newRoles });
                                                        }}
                                                    />
                                                    {role.name}
                                                </label>
                                            </div>
                                        )) : (
                                            <p style={{ color: '#999', fontSize: '0.9em', padding: '10px' }}>Cargando roles...</p>
                                        )}
                                    </div>
                                    <small style={{ color: '#aaa', display: 'block', marginTop: '8px', fontStyle: 'italic' }}>
                                        {settingsForm.allowed_roles.length === 0 ? "⚠️ Atencion: Visible para TODO el PÚBLICO." : `Seleccionados: ${settingsForm.allowed_roles.length} roles.`}
                                    </small>
                                </div>

                                <div className="modal-actions">
                                    <button type="button" className="btn-secondary" onClick={() => setShowSettingsModal(false)}>Cancelar</button>
                                    <button type="submit" className="btn-primary">Guardar</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default AdminNormativa;
